import { trim, isEmpty } from 'lodash';
import { calculateMetrics } from 'utils/analytics/metrics';
import { sumObjectsProperties } from 'utils/objects';
import { ROOM_KEYS, REVENUE_KEYS } from 'utils/analytics/types';

/**
 * Sort data by guaranteed rooms & labels (when rooms are equal)
 * @param  {Object[]} data
 * @return {Object[]} sorted data
 */
const sortData = (data) =>
    [...data].sort((objA, objB) => {
        if (objB.rooms.guaranteed !== objA.rooms.guaranteed) return objB.rooms.guaranteed - objA.rooms.guaranteed;
        if (objA.label < objB.label) return -1;
        if (objA.label > objB.label) return 1;
        return 0;
    });

/**
 * Calculate revenue metrics (adr, revPar, occupancy) from guaranteed figures
 * @param  {Object} row to calculate metrics from
 * @return {Object} an object with the adr, revPar and occupancy
 */
const getRowMetrics = (row) => {
    const guaranteedMetrics = calculateMetrics(
        row.inventory?.availableRoomCount || 0,
        row.rooms.guaranteed,
        row.revenue.accommodation.guaranteed,
    );
    const nonGuaranteedMetrics = calculateMetrics(
        row.inventory?.availableRoomCount || 0,
        row.rooms.nonGuaranteed,
        row.revenue.accommodation.nonGuaranteed,
    );
    return {
        adr: {
            guaranteed: guaranteedMetrics.adr,
            nonGuaranteed: nonGuaranteedMetrics.adr,
        },
        revPar: {
            guaranteed: guaranteedMetrics.revPar,
            nonGuaranteed: nonGuaranteedMetrics.revPar,
        },
        occupancy: {
            guaranteed: guaranteedMetrics.occupancy,
            nonGuaranteed: nonGuaranteedMetrics.occupancy,
        },
    };
};

/**
 * Calculate revenue metrics for each row & handles null labels
 * @param  {Object[]} data
 * @param  {String} missingLabelPlaceholder
 * @return {Object[]} the data with calculated ADR/RevPar/Occupancy
 */
const calculateRowsMetrics = (data, missingLabelPlaceholder) =>
    data.map((row) => ({
        ...row,
        label: trim(row.label) || missingLabelPlaceholder,
        ...getRowMetrics(row),
    }));

/**
 * Calculate totals for an array of data
 * @param  {Object[]} data
 * @param  {String} label - label to set in the row
 * @return {Object} totals for the data
 */
const calculateRowsTotals = (data, label) => {
    if (isEmpty(data)) {
        return {};
    }
    const keys = [...ROOM_KEYS, ...REVENUE_KEYS];
    const totals = {
        ...data.reduce((accu, row) => sumObjectsProperties(accu, row, keys), {}),
        inventory: data[0].inventory,
    };

    return {
        label,
        ...totals,
        ...getRowMetrics(totals),
    };
};

/**
 * Sum data up to 6 rows at most. All extra rows are squashed into a single "others" row
 * @param  {Object[]} rows - rows to sum up
 * @param  {String}   label - label to attach to the summary row
 * @return {Object[]} summed up data (if required) with at most 6 rows
 */
const getDataSummary = (rows, label) => {
    if (rows.length <= 6) return rows;
    return rows.slice(0, 5).concat(calculateRowsTotals(rows.slice(5, rows.length), label));
};

/**
 * Calculate revenue metrics & totals for each segment
 * @param  {Object} data
 * @param  {String} missingLabelPlaceholder - placeholder for rows without a label
 * @param  {String} summaryRowLabel - label for the condensed "Others" row
 * @param  {String} totalsRowLabel - label for the totals row
 * @return {Object} An object with the segments
 */
const process = (data, missingLabelPlaceholder, summaryRowLabel, totalsRowLabel) =>
    Object.entries(data).reduce((accu, [key, value]) => {
        const rows = [...value];
        if (rows === null) return accu;

        const segments = calculateRowsMetrics(sortData(rows), missingLabelPlaceholder);
        const totals = calculateRowsTotals(segments, totalsRowLabel);
        const segmentsSummary = getDataSummary(segments, summaryRowLabel);

        return {
            ...accu,
            [key]: {
                segments,
                segmentsSummary,
                totals,
            },
        };
    }, {});

export default process;
export { sortData };
